import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { useNoIndex } from "../hooks/useNoIndex";
import { CheckCircle2, Loader2, XCircle, Crown, ArrowRight } from "lucide-react";

const POLL_MAX = 8;
const POLL_INTERVAL_MS = 2000;

const CheckoutSuccess = () => {
  useNoIndex();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [plan, setPlan] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= POLL_MAX) {
        setStatus("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan);
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (attemptsRef.current >= POLL_MAX) setStatus("error");
        else setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="washi-bg min-h-screen" data-testid="checkout-success-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 sm:px-12 py-20 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin text-[#E94A3F]" />
            <h1 className="font-display font-black text-4xl mb-3 text-[#1A2A40]">Validation du paiement...</h1>
            <p className="text-[#4A5568]">Quelques secondes, votre accès Premium arrive.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#8CA07A] items-center justify-center mb-6 shadow-brut">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Bienvenue Premium</div>
            <h1 className="font-display font-black text-5xl mb-4 text-[#1A2A40]" data-testid="success-title">
              Paiement confirmé. <span className="font-jp text-[#E94A3F]">ありがとう</span>
            </h1>
            <p className="text-lg text-[#4A5568] mb-10">
              Votre plan <strong>{plan}</strong> est activé. Toutes les leçons et l'IA illimitée sont déverrouillées.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" data-testid="success-dashboard-btn" className="bg-[#E94A3F] text-white font-bold py-4 px-7 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2">
                Aller au dashboard <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/tutor" className="bg-[#1A2A40] text-white font-bold py-4 px-7 rounded-xl hover-lift inline-flex items-center justify-center gap-2">
                Parler à Sensei IA
              </Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-6 text-[#E94A3F]" />
            <h1 className="font-display font-black text-3xl mb-3 text-[#1A2A40]">
              {status === "expired" ? "Session expirée" : status === "timeout" ? "Vérification trop longue" : "Une erreur s'est produite"}
            </h1>
            <p className="text-[#4A5568] mb-8">Réessayez ou contactez le support si le problème persiste.</p>
            <Link to="/pricing" className="inline-flex items-center gap-2 bg-[#E94A3F] text-white font-bold py-3 px-6 rounded-xl shadow-brut-sm">
              Retour aux tarifs <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutSuccess;
